import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, IconButton, ListItem, Skeleton, Stack, styled, Typography } from '@mui/material';
import { Children, FC, PropsWithChildren, useCallback } from 'react';

interface BaseAuditLogDrawerProps extends PropsWithChildren {
    open: boolean;
    error: Error | null;
    loading: boolean;
    isEmpty: boolean;
    onClose: () => void;
}

export const BaseAuditLogDrawer: FC<BaseAuditLogDrawerProps> = ({
    open,
    onClose,
    children,
    error,
    loading,
    isEmpty,
}) => {
    const childrenCount = Children.count(children);

    const closeDrawer = useCallback(() => {
        if (typeof onClose === 'function') {
            onClose();
        }
    }, [onClose]);

    return (
        <StyledDrawer open={open} onClose={closeDrawer} anchor="right">
            <Stack
                sx={{
                    gap: 4,
                    px: 6,
                    py: 4,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography variant="h6">Audit logs</Typography>
                    <IconButton edge="end" onClick={closeDrawer}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                {error && <Typography variant="body2">Error loading audit logs: {error.message}</Typography>}
                {loading && (
                    <Stack
                        sx={{
                            flex: 1,
                            gap: 2,
                            overflow: 'auto',
                            width: 475,
                        }}
                    >
                        <SkeletonAuditListItem></SkeletonAuditListItem>
                        <SkeletonAuditListItem></SkeletonAuditListItem>
                    </Stack>
                )}
                {!loading && !error && isEmpty && (
                    <Typography
                        variant="body2"
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        No audit logs to display
                    </Typography>
                )}
                {!loading && !error && childrenCount > 0 && children}
            </Stack>
        </StyledDrawer>
    );
};

export const AuditLogListItem = styled(ListItem)(({ theme }) => ({
    backgroundColor: theme.palette.grey[200],
    borderRadius: 4,
}));

const SkeletonAuditListItem = () => {
    return <Skeleton variant="rectangular" width={'100%'} height={108} />;
};

const StyledDrawer = styled(Drawer)(({ theme }) => ({
    '&.MuiDrawer-root': {
        // Ensure the drawer appears above other elements and above the FullScreenContainer when open
        zIndex: theme.zIndex.fullScreenContainer + 1,
    },
}));
